import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Head, usePage } from '@inertiajs/react'
import classNames from 'classnames'
import ReactConfetti from 'react-confetti'
import { getApps, initializeApp } from 'firebase/app'
import { getDatabase, onValue, ref } from 'firebase/database'

type Winner = {
  name: string
  category: string
  prize: string
} | null

type LuckyDrawAppearance = {
  backgroundColor?: string | null
  backgroundPosition?: string | null
  fontFamily?: string | null
  fontColor?: string | null
  backgroundImageUrl?: string | null
}

type PageProps = {
  eventSlug: string
  firebasePathPrefix: string
  registrantNames: string[]
  currentWinner: Winner
  luckyDrawAppearance?: LuckyDrawAppearance
}

type WinnerPayload = {
  winnerName: string | null
  prize: string | null
  category: string | null
}

const firebaseConfig = {
  apiKey: 'AIzaSyAIQuNFMPrYUETJYN6ClgAV3-1Naw7Eb5A',
  authDomain: 'ava-platform-bdc67.firebaseapp.com',
  databaseURL: 'https://ava-platform-bdc67-default-rtdb.asia-southeast1.firebasedatabase.app',
  projectId: 'ava-platform-bdc67',
  storageBucket: 'ava-platform-bdc67.firebasestorage.app',
  messagingSenderId: '701056514018',
  appId: '1:701056514018:web:f8fae3f9e6b1231c1f6bbb',
}

const firebaseApp = getApps().length ? getApps()[0] : initializeApp(firebaseConfig)
const database = getDatabase(firebaseApp)

const DRAW_DURATION = 4000
const TARGET_COUNT = 20
const READY_TEXT = 'Are You Ready?'

const FIRST_NAMES = [
  'Alex',
  'Jamie',
  'Taylor',
  'Jordan',
  'Casey',
  'Morgan',
  'Riley',
  'Avery',
  'Cameron',
  'Quinn',
  'Skyler',
  'Hayden',
  'Rowan',
  'Emerson',
  'Logan',
  'Reese',
  'Parker',
  'Sage',
  'Bailey',
  'Drew',
]

const LAST_NAMES = [
  'Tan',
  'Lee',
  'Ng',
  'Lim',
  'Wong',
  'Chia',
  'Ong',
  'Chong',
  'Teo',
  'Goh',
  'Low',
  'Koh',
  'Yeo',
  'Ho',
  'Ang',
  'Toh',
  'Cheng',
  'Neo',
  'Lau',
  'Quek',
]

const shuffle = (items: string[]) => {
  const result = [...items]
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = result[i]
    result[i] = result[j]
    result[j] = temp
  }
  return result
}

const generateFakeNames = (count: number) =>
  Array.from({ length: count }, () => {
    const first = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)]
    const last = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)]
    return `${first} ${last}`
  })

const buildNameList = (winnerName: string, registrantNames: string[]) => {
  const normalizedWinner = winnerName.trim().toLowerCase()
  const cleaned = registrantNames
    .map((name) => name.trim())
    .filter((name) => name.length > 0 && name.toLowerCase() !== normalizedWinner)

  const sampled = shuffle(cleaned).slice(0, TARGET_COUNT)
  if (sampled.length < TARGET_COUNT) {
    sampled.push(...generateFakeNames(TARGET_COUNT - sampled.length))
  }

  return [READY_TEXT, ...sampled, winnerName]
}

const LuckyDrawScreen = () => {
  const {
    eventSlug,
    firebasePathPrefix,
    registrantNames = [],
    currentWinner,
    luckyDrawAppearance = {},
  } = usePage<PageProps>().props
  // Cleared appearance fields are persisted as empty strings, which `??` does
  // not treat as missing. Normalise them to null so the defaults kick in.
  const presence = (value?: string | null) => {
    const trimmed = value?.trim()
    return trimmed ? trimmed : null
  }
  // Attachment URLs carry the original filename, which Rails leaves unescaped
  // for characters like ( ) and '. An unquoted CSS url() silently drops those.
  const cssUrl = (url: string) => `url("${url.replace(/["\\]/g, '\\$&')}")`
  const backgroundColor = presence(luckyDrawAppearance.backgroundColor)
  const backgroundPosition = presence(luckyDrawAppearance.backgroundPosition)
  const fontFamily = presence(luckyDrawAppearance.fontFamily)
  const fontColor = presence(luckyDrawAppearance.fontColor)
  const backgroundImageUrl = presence(luckyDrawAppearance.backgroundImageUrl)
  const containerRef = useRef<HTMLDivElement>(null)
  const animationFrameRef = useRef<number | null>(null)
  const drawTimeoutRef = useRef<number | null>(null)
  const confettiTimeoutRef = useRef<number | null>(null)
  const animationTokenRef = useRef(0)
  const [startConfetti, setStartConfetti] = useState(false)
  const [data, setData] = useState<string[]>([])
  const [winnerPayload, setWinnerPayload] = useState<WinnerPayload>({
    winnerName: currentWinner?.name ?? null,
    prize: currentWinner?.prize ?? null,
    category: currentWinner?.category ?? null,
  })

  const scrollToBottom = (container: HTMLDivElement, duration: number) => {
    animationTokenRef.current += 1
    const token = animationTokenRef.current
    const start = container.scrollTop
    const end = container.scrollHeight - container.clientHeight
    const distance = end - start
    const startTime = performance.now()

    const easeOutQuad = (t: number) => t * (2 - t)

    const animateScroll = (currentTime: number) => {
      if (token !== animationTokenRef.current) {
        return
      }
      const timeElapsed = currentTime - startTime
      const progress = Math.min(timeElapsed / duration, 1)
      const easedProgress = easeOutQuad(progress)

      container.scrollTop = start + distance * easedProgress

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animateScroll)
      } else if (Math.abs(container.scrollTop - end) <= 1) {
        setStartConfetti(true)
        if (confettiTimeoutRef.current) {
          window.clearTimeout(confettiTimeoutRef.current)
        }
        confettiTimeoutRef.current = window.setTimeout(() => {
          setStartConfetti(false)
        }, 10000)
      }
    }

    animationFrameRef.current = requestAnimationFrame(animateScroll)
  }

  const startDraw = () => {
    setStartConfetti(false)
    if (containerRef.current) {
      scrollToBottom(containerRef.current, DRAW_DURATION)
    }
  }

  const stopAnimation = () => {
    animationTokenRef.current += 1
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    if (drawTimeoutRef.current) {
      window.clearTimeout(drawTimeoutRef.current)
      drawTimeoutRef.current = null
    }
    if (confettiTimeoutRef.current) {
      window.clearTimeout(confettiTimeoutRef.current)
      confettiTimeoutRef.current = null
    }
    setStartConfetti(false)
  }

  useEffect(() => {
    const basePath = firebasePathPrefix?.length ? `/${firebasePathPrefix}/${eventSlug}` : `/${eventSlug}`
    const dbRef = ref(database, basePath)

    const unsubscribe = onValue(dbRef, (snapshot) => {
      const payload = snapshot.val() || {}
      const winnerName = payload.winnerName ?? null
      const prize = payload.prize ?? null
      const category = payload.category ?? null

      setWinnerPayload({ winnerName, prize, category })
      stopAnimation()

      if (!winnerName) {
        setData([READY_TEXT])
        setStartConfetti(false)
        if (containerRef.current) {
          containerRef.current.scrollTop = 0
        }
        return
      }

      setData(buildNameList(winnerName, registrantNames))

      if (containerRef.current) {
        containerRef.current.scrollTop = 0
      }

      drawTimeoutRef.current = window.setTimeout(() => {
        startDraw()
      }, 50)
    })

    return () => {
      unsubscribe()
      stopAnimation()
    }
  }, [eventSlug, registrantNames])

  const backgroundStyle: CSSProperties = backgroundImageUrl
    ? {
        backgroundImage: cssUrl(backgroundImageUrl),
        backgroundSize: 'cover',
        backgroundPosition: backgroundPosition || 'center',
        backgroundRepeat: 'no-repeat',
      }
    : { background: backgroundColor ?? '#00FF00' }

  const titleStyle: CSSProperties = {
    ...(fontFamily ? { fontFamily } : {}),
    ...(fontColor ? { color: fontColor } : {}),
  }

  const nameStyle: CSSProperties = {
    ...(fontFamily ? { fontFamily } : {}),
    ...(fontColor ? { color: fontColor } : {}),
  }

  return (
    <div className="h-dvh w-full flex flex-col overflow-hidden">
      <Head title="Lucky Draw" />
      {/* Dark top bar spanning the full width with the centered title */}
      <div className="shrink-0 w-full bg-dark flex justify-center py-4">
        <h1
          className={classNames(
            'font-extrabold uppercase text-9xl text-center',
            fontColor ? '' : 'text-ava-blue-07'
          )}
          style={titleStyle}
        >
          Lucky Draw
        </h1>
      </div>

      {/* KV region fills all remaining height below the bar (no white strips) */}
      <div
        className="relative flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden"
        style={backgroundStyle}
      >
        <ReactConfetti
          gravity={0.05}
          className={classNames(
            'absolute inset-0 h-full w-full transition-all duration-300',
            startConfetti ? 'opacity-100' : 'opacity-0'
          )}
        />
        <div className="bg-dark text-white rounded-full aspect-square min-w-[480px] h-[480px] max-h-[480px] flex justify-center flex-col items-center overflow-hidden relative z-10">
          <div
            ref={containerRef}
            className={classNames(
              'h-full bg-transparent w-full overflow-y-scroll text-center text-3xl scrollbar-hide',
              fontColor ? '' : 'text-white'
            )}
            style={{ ...(fontFamily ? { fontFamily } : {}) }}
          >
            {data.length === 1 && data[0] === READY_TEXT ? (
              <div className="h-full flex items-center justify-center">
                <p
                  className="text-5xl w-full flex-shrink-0 leading-none font-semibold uppercase"
                  style={nameStyle}
                >
                  {READY_TEXT}
                </p>
              </div>
            ) : (
              (data || []).map((name, index) => (
                <div
                  key={`${name}-${index}`}
                  className="h-full flex items-center justify-center"
                >
                  <p
                    className="text-5xl w-full flex-shrink-0 leading-none font-semibold uppercase"
                    style={nameStyle}
                  >
                    {name}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LuckyDrawScreen
